import { RealtimeChannel } from "@supabase/supabase-js";
import { uuid } from "@/interfaces";
import supabase from "@/lib/supabase.ts";
import { ChatRoomMessage } from "@/models/chat.model.ts";
import { store } from "@/lib/store.ts";
import { addNew, replaceTemp } from "@/app/user/messages/chat-rooms.slice.ts";
import { touchRoom, updateUnreadCount } from "@/app/user/messages/messages.slice.ts";
import { ChatService } from "@/services/chat.service.ts";
import { setTyping } from "@/lib/store/realtime.slice.ts";

class RealtimeService {
  /**
   * Chat related channels.
   *
   * Global - for user-scoped room membership and unread updates.
   * Chat room - for subscribing to the currently viewed room.
   */
  private globalChatFeed: RealtimeChannel | null = null;
  private globalChatFeedUserId: uuid | null = null;
  private chatRoomChannel: RealtimeChannel | null = null;
  private currentRoomId: uuid | null = null;
  private chatListRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Initialize user-scoped chat subscriptions.
   */
  initializeGlobalChatFeed(userId: uuid) {
    if (this.globalChatFeed && this.globalChatFeedUserId === userId) return;

    this.closeGlobalChatFeed();
    this.globalChatFeedUserId = userId;

    this.globalChatFeed = supabase
      .channel(`chat_user:${userId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_room_users", filter: `user=eq.${userId}` },
        () => this.scheduleChatListRefresh()
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "chat_room_users", filter: `user=eq.${userId}` },
        ({ new: row }) => {
          store.dispatch(updateUnreadCount({ roomId: row.room as uuid, count: row.count as number }));
          this.scheduleChatListRefresh();
        }
      )
      .subscribe();
  }

  /**
   * Initializes subscription to a single chat room.
   *
   * This chat room subscription handles user presence, typing indicators, messages, etc.
   *
   * @param {uuid} roomId
   */
  openRoom(roomId: uuid) {
    if (this.currentRoomId == roomId) return;

    this.closeRoom();

    this.currentRoomId = roomId;

    this.chatRoomChannel = supabase.channel(`room:${roomId}`, {
      config: {
        presence: { key: store.getState().auth.user?.id },
      },
    });

    this.chatRoomChannel.on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "chat_room_messages",
        filter: `room=eq.${roomId}`,
      },
      (payload) => this.onRoomInsert(payload.new as ChatRoomMessage)
    );

    this.chatRoomChannel.on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "chat_room_messages",
        filter: `room=eq.${roomId}`,
      },
      (payload) => this.onRoomUpdate(payload.new as ChatRoomMessage)
    );

    this.chatRoomChannel.on("broadcast", { event: "typing" }, ({ payload }) => this.onTypingBroadcast(payload));

    return this.chatRoomChannel.subscribe();
  }

  closeRoom() {
    if (this.chatRoomChannel) this.chatRoomChannel.unsubscribe();

    this.chatRoomChannel = null;
    this.currentRoomId = null;
  }

  shutdown() {
    this.closeGlobalChatFeed();
    this.closeRoom();
  }

  async broadcastTyping(roomId: uuid, isTyping: boolean) {
    if (roomId !== this.currentRoomId || !this.chatRoomChannel) return;

    return this.chatRoomChannel.send({
      type: "broadcast",
      event: "typing",
      payload: {
        userId: store.getState().auth.user?.id,
        typing: isTyping,
        ts: Date.now(),
      },
    });
  }

  private onRoomInsert(msg: ChatRoomMessage) {
    if (msg.id.startsWith("tmp-")) return;

    const messages = store.getState().chatRooms.byRoom[msg.room]?.list ?? [];
    if (messages.some((m) => m.id === msg.id)) return;

    const tempId = this.findMatchingTempMessageId(messages, msg);
    if (tempId) {
      store.dispatch(replaceTemp({ roomId: msg.room, tempId, real: msg }));
    } else {
      store.dispatch(addNew({ roomId: msg.room, message: msg }));
    }

    store.dispatch(touchRoom({ roomId: msg.room, last: msg }));
    void ChatService.ensureSendersCached(msg.room, [msg.sender]).catch((error) => {
      console.error("Failed to load chat members:", error);
    });
    void ChatService.markRoomRead(msg.room).catch((error) => {
      console.error("Failed to mark chat room read:", error);
    });
  }

  private onRoomUpdate(msg: ChatRoomMessage) {
    const messages = store.getState().chatRooms.byRoom[msg.room]?.list ?? [];
    if (messages.some((m) => m.id === msg.id)) {
      store.dispatch(replaceTemp({ roomId: msg.room, tempId: msg.id, real: msg }));
    }
  }

  private onTypingBroadcast(payload: { userId: uuid; typing: boolean; ts: number }) {
    store.dispatch(
      setTyping({
        roomId: this.currentRoomId!,
        userId: payload.userId,
        typing: payload.typing,
        ts: payload.ts,
      })
    );
  }

  private closeGlobalChatFeed() {
    if (this.chatListRefreshTimer) {
      clearTimeout(this.chatListRefreshTimer);
      this.chatListRefreshTimer = null;
    }

    if (this.globalChatFeed) this.globalChatFeed.unsubscribe();

    this.globalChatFeed = null;
    this.globalChatFeedUserId = null;
  }

  private scheduleChatListRefresh() {
    if (this.chatListRefreshTimer) clearTimeout(this.chatListRefreshTimer);

    this.chatListRefreshTimer = setTimeout(() => {
      this.chatListRefreshTimer = null;
      ChatService.listChats().catch((error) => {
        console.error("Failed to refresh chat rooms:", error);
      });
    }, 250);
  }

  private findMatchingTempMessageId(messages: ChatRoomMessage[], msg: ChatRoomMessage): string | null {
    const incomingCreatedAt = new Date(msg.created_at).getTime();
    let bestMatch: { id: string; ageDifference: number } | null = null;

    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const candidate = messages[index];
      if (!candidate.id.startsWith("tmp-")) continue;
      if (candidate.sender !== msg.sender) continue;
      if (candidate.message !== msg.message) continue;
      if (candidate.is_file !== msg.is_file) continue;
      if (candidate.file_type !== msg.file_type) continue;
      if (candidate.is_system !== msg.is_system) continue;

      const candidateCreatedAt = new Date(candidate.created_at).getTime();
      if (!Number.isFinite(incomingCreatedAt) || !Number.isFinite(candidateCreatedAt)) continue;
      const ageDifference = Math.abs(incomingCreatedAt - candidateCreatedAt);
      if (ageDifference > 30_000) continue;

      if (!bestMatch || ageDifference < bestMatch.ageDifference) {
        bestMatch = { id: candidate.id, ageDifference };
      }
    }

    return bestMatch?.id ?? null;
  }
}

export const realtime = new RealtimeService();
